import React, { useState, useMemo } from 'react'
import {
  Image,
  Button,
  Pill,
  Filter,
  TableComponents,
  useModalContext,
  GenericError,
} from '@sketch/components'
import { ReactComponent as ExclamationMarkIcon } from '@sketch/icons/exclamation-mark-triangle-64'

import {
  GetIntegrationConnectUrlMutationVariables,
  useGetWorkspaceIntegrationsQuery,
} from '@sketch/gql-types'

import Slack from '@sketch/icons/slack@1x.png'
import SlackRetina from '@sketch/icons/slack@2x.png'

import { ConnectIntegrationModal, IntegrationSettingsModal } from '../../modals'

import {
  Wrapper,
  SearchWrapper,
  Table,
  IntegrationInfo,
  IntegrationIcon,
  IntegrationDetails,
  IntegrationName,
  IntegrationDescription,
  IntegrationStatus,
  StyledTableCell,
  ActionCell,
  Divider,
  EmptyState,
  EmptyCell,
  EmptyRow,
  ErrorWrapper,
  StyledFakeLinkButton,
} from './WorkspaceIntegrations.styles'

import { IntegrationTabs } from './IntegrationTabs'
import { SkeletonRow } from './SkeletonRow'

const { TableRow } = TableComponents

// Minimum number of integrations required to show tabs and search
const MIN_INTEGRATIONS_FOR_FILTERS = 5

const TABLE_HEADER = [{ label: 'Integration' }, { label: 'Action' }]

interface Integration {
  type: 'integration'
  id: string
  name: string
  description: string
  icon: string
  icon2x: string
  isConnected: boolean
  integrationProvider: GetIntegrationConnectUrlMutationVariables['provider']
}

interface SkeletonItem {
  type: 'skeleton'
  id: string
}

interface DividerItem {
  type: 'divider'
  id: string
}

type TableItem = Integration | SkeletonItem | DividerItem

const AVAILABLE_INTEGRATIONS: Integration[] = [
  {
    type: 'integration',
    id: 'slack',
    name: 'Slack',
    description:
      'Connect your Sketch Workspace to Slack and see previews when you share documents. Workspace members can also get comment and reply notifications.',
    integrationProvider: 'SLACK',
    icon: Slack,
    icon2x: SlackRetina,
    isConnected: false,
  },
]

const DIVIDER_ITEM: DividerItem = { type: 'divider', id: 'divider' }

// Derive skeleton array from available integrations so we show the correct number of skeletons
const SKELETON_ITEMS: SkeletonItem[] = AVAILABLE_INTEGRATIONS.map(
  (_, index) => ({
    type: 'skeleton',
    id: `skeleton-${index}`,
  })
)

type TabType = 'all' | 'enabled' | 'not-configured'

export interface WorkspaceIntegrationsProps {
  workspaceId: string
}

export const WorkspaceIntegrations: React.FC<WorkspaceIntegrationsProps> = ({
  workspaceId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const { showModal } = useModalContext()

  const { data, loading, error } = useGetWorkspaceIntegrationsQuery({
    variables: { identifier: workspaceId },
  })

  // Merge available integrations with connected integrations from the API
  const allIntegrations: Integration[] = useMemo(() => {
    const connectedIntegrations = data?.workspace.integrations || []

    return AVAILABLE_INTEGRATIONS.map(availableIntegration => {
      const isConnected = connectedIntegrations.some(
        apiIntegration =>
          apiIntegration.integrationProvider ===
          availableIntegration.integrationProvider
      )

      return { ...availableIntegration, isConnected }
    })
  }, [data?.workspace.integrations])

  const filteredConnectedIntegrations = useMemo(() => {
    let integrations = allIntegrations.filter(int => int.isConnected)

    if (searchQuery) {
      integrations = integrations.filter(integration =>
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (activeTab === 'not-configured') {
      return []
    }

    return integrations
  }, [allIntegrations, searchQuery, activeTab])

  const filteredAvailableIntegrations = useMemo(() => {
    let integrations = allIntegrations.filter(int => !int.isConnected)

    if (searchQuery) {
      integrations = integrations.filter(integration =>
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (activeTab === 'enabled') {
      return []
    }

    return integrations
  }, [allIntegrations, searchQuery, activeTab])

  const filteredIntegrations: TableItem[] = useMemo(() => {
    const connected = filteredConnectedIntegrations
    const available = filteredAvailableIntegrations

    // Add divider between connected and available if both exist
    if (
      connected.length > 0 &&
      available.length > 0 &&
      activeTab !== 'enabled'
    ) {
      return [...connected, DIVIDER_ITEM, ...available]
    }

    return [...connected, ...available]
  }, [filteredConnectedIntegrations, filteredAvailableIntegrations, activeTab])

  const totalIntegrations = allIntegrations.length
  const enabledCount = filteredConnectedIntegrations.length
  const availableCount = filteredAvailableIntegrations.length

  const showFilters = totalIntegrations >= MIN_INTEGRATIONS_FOR_FILTERS

  const hasResults =
    filteredConnectedIntegrations.length > 0 ||
    filteredAvailableIntegrations.length > 0

  const handleConnect = (integration: Integration) => {
    showModal(ConnectIntegrationModal, {
      integrationName: integration.name,
      integrationProvider: integration.integrationProvider,
      workspaceId,
    })
  }

  const handleSettings = (integration: Integration) => {
    showModal(IntegrationSettingsModal, {
      integrationName: integration.name,
      integrationProvider: integration.integrationProvider,
      workspaceId,
    })
  }

  const renderTableItem = (item: TableItem) => {
    if (item.type === 'skeleton') {
      return <SkeletonRow key={item.id} />
    }

    if (item.type === 'divider') {
      return (
        <EmptyRow key={item.id}>
          <EmptyCell colSpan={2}>
            <Divider />
          </EmptyCell>
        </EmptyRow>
      )
    }

    return (
      <TableRow key={item.id}>
        <StyledTableCell>
          <IntegrationInfo>
            <IntegrationIcon>
              <Image
                alt={item.name}
                src={item.icon}
                srcSet={`${item.icon2x} 2x`}
                width="32px"
                height="32px"
              />
            </IntegrationIcon>
            <IntegrationDetails>
              {item.isConnected ? (
                <IntegrationStatus>
                  <IntegrationName>{item.name}</IntegrationName>
                  <Pill variant="owner">Enabled</Pill>
                </IntegrationStatus>
              ) : (
                <IntegrationName>{item.name}</IntegrationName>
              )}
              <IntegrationDescription>
                {item.description}
              </IntegrationDescription>
            </IntegrationDetails>
          </IntegrationInfo>
        </StyledTableCell>
        <StyledTableCell>
          <ActionCell>
            {item.isConnected ? (
              <Button
                variant="secondary-untinted"
                size="32"
                onClick={() => handleSettings(item)}
              >
                Settings…
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="32"
                onClick={() => handleConnect(item)}
              >
                Connect…
              </Button>
            )}
          </ActionCell>
        </StyledTableCell>
      </TableRow>
    )
  }

  if (error) {
    return (
      <ErrorWrapper>
        <GenericError
          title="Integrations Unavailable"
          description="There was a problem retrieving the integrations for this workspace."
          icon={<ExclamationMarkIcon />}
          iconSize="medium"
          extra={
            <StyledFakeLinkButton
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </StyledFakeLinkButton>
          }
        />
      </ErrorWrapper>
    )
  }

  return (
    <Wrapper>
      {showFilters && (
        <>
          <IntegrationTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            totalIntegrations={totalIntegrations}
            enabledCount={enabledCount}
            availableCount={availableCount}
          />

          <SearchWrapper>
            <Filter
              data-test-id="integrations-search-filter"
              name="integrations-search-filter"
              placeholder="Search integrations..."
              onChange={setSearchQuery}
              value={searchQuery}
              fullWidth
            />
          </SearchWrapper>
        </>
      )}

      {!hasResults && !loading ? (
        <EmptyState>No integrations found.</EmptyState>
      ) : (
        <Table
          header={TABLE_HEADER}
          items={loading ? SKELETON_ITEMS : filteredIntegrations}
          renderItem={renderTableItem}
          hideHeader
        />
      )}
    </Wrapper>
  )
}
